import { supabase } from '@/integrations/supabase/client';
import { generateEmbedding1536 } from '@/lib/embeddings';
import { fetchVault } from '@/lib/librarian/api';
import { hydrateGraphRetrievalHits, retrieveGraphAugmentedResults } from '@/lib/librarian/graph';
import { semanticSearch } from '@/lib/semanticSearch';
import type { LibrarianSearchResult } from '@/lib/librarian/types';

export function useLibrarianSearch() {
  const search = async (knowledgeBaseId: string, query: string) => {
    const vault = await fetchVault(knowledgeBaseId);
    const results: LibrarianSearchResult[] = [];

    if (vault) {
      const lexicalQuery = `%${query.toLowerCase()}%`;
      const embedding = await generateEmbedding1536(query);

      const lexicalPromise = supabase
        .from('knowledge_base_vault_pages')
        .select('*')
        .eq('vault_id', vault.id)
        .or(`title.ilike.${lexicalQuery},path.ilike.${lexicalQuery},content_markdown.ilike.${lexicalQuery}`)
        .limit(6);

      const semanticPromise = embedding
        ? (supabase.rpc as any)('match_vault_pages', {
            query_embedding: embedding,
            match_threshold: 0.2,
            match_count: 6,
            p_vault_id: vault.id,
          })
        : Promise.resolve({ data: [], error: null });

      const [{ data: lexicalData, error: lexicalError }, { data: semanticData, error: semanticError }] =
        await Promise.all([lexicalPromise, semanticPromise]);

      if (lexicalError) throw lexicalError;
      if (semanticError) throw semanticError;

      const merged = new Map<string, LibrarianSearchResult>();
      (lexicalData ?? []).forEach((page: any) => {
        merged.set(`vault:${page.id}`, {
          kind: 'vault',
          id: page.id,
          title: page.title,
          path: page.path,
          pageType: page.page_type,
          content: page.content_markdown,
          similarity: 0.45,
          sourceDocumentIds: Array.isArray(page.source_document_ids) ? page.source_document_ids : [],
          retrievalReason: 'Direct vault lexical match',
        });
      });

      (semanticData ?? []).forEach((page: any) => {
        merged.set(`vault:${page.id}`, {
          kind: 'vault',
          id: page.id,
          title: page.title,
          path: page.path,
          pageType: page.page_type,
          content: page.content_markdown,
          similarity: Number(page.similarity ?? 0),
          sourceDocumentIds: Array.isArray(page.source_document_ids) ? page.source_document_ids : [],
          retrievalReason: 'Direct vault semantic match',
        });
      });

      const graphHits = await retrieveGraphAugmentedResults(knowledgeBaseId, query);
      const hydratedGraphResults = await hydrateGraphRetrievalHits(knowledgeBaseId, graphHits);
      hydratedGraphResults.forEach((result) => {
        merged.set(`${result.kind}:${result.id}`, result);
      });

      results.push(...Array.from(merged.values()).sort((a, b) => b.similarity - a.similarity));
    }

    const sourceMatches = await semanticSearch(knowledgeBaseId, query, { limit: 5, threshold: 0.2 });
    results.push(
      ...sourceMatches.map((document) => ({
        kind: 'source' as const,
        id: document.id,
        title: document.name,
        content: document.content,
        similarity: document.similarity ?? 0,
        retrievalReason: 'Direct source semantic match',
      }))
    );

    const mergedResults = new Map<string, LibrarianSearchResult>();
    results.forEach((result) => {
      const key = `${result.kind}:${result.id}`;
      const existing = mergedResults.get(key);
      if (!existing || result.similarity > existing.similarity) {
        mergedResults.set(key, result);
      }
    });

    return Array.from(mergedResults.values()).sort((a, b) => {
      if (a.kind !== b.kind) return a.kind === 'vault' ? -1 : 1;
      return b.similarity - a.similarity;
    });
  };

  return { search };
}
